import { ChangeEvent, useEffect, useMemo, useState } from "react"

import PopupFooter from "../../Popups/PopupFooter";
import PopupHeader from "../../Popups/PopupHeader";
import ModalBase from "../../Popups/ModalBase";
import TruncatedText from "../../Page/TruncatedText";
import { splitLabel } from "../../../utils/formattingFns";
import SelectField from "../../Form/SelectFields";
import InputField from "../../Form/InputField";


export default function TagCell({
    value,
    label,
    hasPadding,
}: {
    value: string[],
    label: string,
    hasPadding: boolean,
}) {
    const [ shownTags, setShownTags ] = useState<string[]>([]);
    const [ tagCategoryOptions, setTagCategoryOptions ] = useState<{ value: string, label: string }[]>([]);
    const [ selectedCategory, setSelectedCategory ] = useState<{ value: string, label: string }>({ value: 'all', label: 'All' });
    const [ searchInput, setSearchInput ] = useState('');

    const [ open, setOpen ] = useState(false);
   
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSelectedCategory({ value: 'all', label: 'All' });
    };

    useEffect(() => {
        if (value.length > 0) {
            if (value.length > 1) {
                const tags = value.slice(0,1);
                setShownTags(tags);
            } else {
                setShownTags(value);
            }

            let categories: string[] = [];
            value.forEach(tag => categories.push(tag?.includes('::') ? tag?.split('::')[0] : 'general'));
            categories = Array.from(new Set(categories)).sort();

            setTagCategoryOptions([{ value: 'all', label: 'All' }].concat(categories.map(category => ({ value: category, label: splitLabel(category) }))));
        }
        
    },[value]);

    const selectedTags: string[] = useMemo(() => {
        if (value.length === 0) return [];

        const selectedCategoryValue = selectedCategory.value;
        const sortedValues = value.sort();

        if (selectedCategoryValue === 'all') return sortedValues;

        if (selectedCategoryValue === 'general') return sortedValues.filter(val => !val?.includes('::'));

        return sortedValues.filter(val => val.split('::')[0] === selectedCategoryValue);

    }, [value, selectedCategory]);

    const filteredTags: string[] = useMemo(() => {
        if (selectedTags.length === 0) return [];

        return selectedTags.filter(tag => tag?.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()));
    }, [searchInput, selectedTags]);
    
    const handleSelect = (e: any, name: string) => {
        setSelectedCategory(e);
        setSearchInput('');
    }

    const handleSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index?: number) => {
        setSearchInput(e.target.value);
    }

    return (
            <div className={`flex justify-center items-center gap-2 ${hasPadding && 'py-4 px-6'} w-[200px]`}>
                {value[0] !== null && value.length > 0 ? (
                    <>
                    {Array.isArray(shownTags) && shownTags.map((tag, i) => (
                        <div key={i} className="py-1 px-3 rounded-lg bg-secondary max-w-full">
                            <TruncatedText text={tag} alignment='center'>
                                <p className="text-primary font-semibold text-[12px] text-nowrap max-w-full truncate">{tag}</p>
                            </TruncatedText>
                        </div>
                    ))}
                    {value.length > 1 && (
                        <div>
                            <button onClick={handleOpen} className="py-1 px-2 rounded-lg hover:bg-black/[0.05] transition-all">
                                <p className="text-[12px] font-semibold text-disable">+{value.length-1}</p>
                            </button>

                            <ModalBase
                                open={open}
                                modalId={`${label.toLocaleLowerCase()}-modal`}
                            >
                                <div className={`bg-white rounded-lg w-[600px] popup-max-height mx-auto`}>
                                    <div className='flex flex-col justify-center items-stretch popup-max-height'>
                                        <PopupHeader label={label} closeFn={handleClose} hasOutline={false}/>

                                        <div className="w-full flex flex-col justify-start items-stretch gap-4 p-6">
                                            <div className="w-full flex justify-start items-center gap-4">
                                                <div className='w-1/3 shrink-0'>
                                                    <SelectField 
                                                        label="Category"
                                                        id='tag-category'
                                                        name='tag-category'
                                                        value={selectedCategory}
                                                        options={tagCategoryOptions}
                                                        isRequired={false}
                                                        isCreatable={false}
                                                        isMulti={false}
                                                        menuPortalTarget={document.getElementById(`${label.toLocaleLowerCase()}-modal`)}
                                                        handleChange={handleSelect}
                                                    />
                                                </div>
                                                <div className="grow">
                                                    <InputField 
                                                        label="Search Tag"
                                                        type='text'
                                                        id='search-tag'
                                                        name='search-tag'
                                                        value={searchInput}
                                                        isRequired={false}
                                                        handleChange={handleSearch}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-full border border-outline rounded-lg p-2">
                                                <div className="w-full flex justify-start items-start gap-2 flex-wrap max-h-[200px] overflow-y-auto">
                                                {filteredTags.length > 0 ? (
                                                    <>
                                                    {filteredTags.map((tag, i) => (
                                                        <div key={i} className="py-1 px-3 rounded-lg bg-secondary text-primary font-semibold text-[12px]">{tag}</div>
                                                    ))}
                                                    </>
                                                ) : (
                                                     <div className="w-full h-full flex justify-center items-center">
                                                        <p className="text-disable">No tags selected</p>
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        </div>

                                        <PopupFooter 
                                            buttons={[{
                                                label: 'Close',
                                                type: 'primary',
                                                styling: 'solid',
                                                id: 'close-tags-btn',
                                                action: handleClose,
                                            }]}
                                            hasOutline={false}
                                        />
                                    </div>
                                </div>
                            </ModalBase>
                        </div>
                    )}
                    </>
                ) : (
                    <p className="text-[14px] text-nowrap font-medium text-default">None</p>
                )}
                
            </div>
    )
}